// Phase 10 — useDashboardWidgets hook.
//
// Read-only consumer of dashboard_snapshots table.
// Returns widget data for user and admin dashboards.

import { useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ─────────────────────────────────────────────────────

export type WidgetTrend = "up" | "down" | "flat" | "new";

export interface DashboardWidget {
  id: string;
  widget_key: string;
  widget_label: string;
  category: string;
  value_primary: number | null;
  value_secondary: number | null;
  value_pct: number | null;
  value_text: string | null;
  value_json: Record<string, unknown>;
  trend: WidgetTrend | null;
  trend_pct: number | null;
  sort_order: number;
  snapshot_at: string;
}

export interface UseDashboardWidgetsState {
  widgets: readonly DashboardWidget[];
  loading: boolean;
  error: string | null;
}

// ─── Parser ────────────────────────────────────────────────────

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseWidget(row: Record<string, unknown>): DashboardWidget {
  return {
    id: String(row.id ?? ""),
    widget_key: String(row.widget_key ?? ""),
    widget_label: String(row.widget_label ?? ""),
    category: String(row.category ?? "user"),
    value_primary: asNum(row.value_primary),
    value_secondary: asNum(row.value_secondary),
    value_pct: asNum(row.value_pct),
    value_text: asStr(row.value_text),
    value_json: typeof row.value_json === "object" && row.value_json !== null
      ? (row.value_json as Record<string, unknown>)
      : {},
    trend: asStr(row.trend) as WidgetTrend | null,
    trend_pct: asNum(row.trend_pct),
    sort_order: typeof row.sort_order === "number" ? row.sort_order : 0,
    snapshot_at: String(row.snapshot_at ?? ""),
  };
}

// ─── Hook ──────────────────────────────────────────────────────

export function useDashboardWidgets(
  category: "user" | "admin" = "user",
): UseDashboardWidgetsState {
  const { user } = useAuthSession();
  const [widgets, setWidgets] = useState<readonly DashboardWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setWidgets([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      const resp = await (
        client.from("dashboard_snapshots" as never) as unknown as {
          select: (cols: string) => {
            eq: (col: string, val: string) => {
              order: (col: string, opts: { ascending: boolean }) => Promise<{
                data: Record<string, unknown>[] | null;
                error: { message: string } | null;
              }>;
            };
          };
        }
      )
        .select("*")
        .eq("category", category)
        .order("sort_order", { ascending: true });

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setWidgets([]);
        setLoading(false);
        return;
      }

      setWidgets(
        (resp.data ?? [])
          .filter((r): r is Record<string, unknown> => r !== null)
          .map(parseWidget),
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, category]);

  return useMemo(
    () => ({ widgets, loading, error }),
    [widgets, loading, error],
  );
}
